import React, { createContext, useContext, useEffect, useState } from 'react';

import { CreateUserRequest } from '@wb-quaero/camper-interface-public/api';
import { isAxiosError } from 'axios';
import { FirebaseError, initializeApp } from 'firebase/app';
import {
  AuthErrorCodes,
  AuthProvider,
  EmailAuthProvider,
  FacebookAuthProvider,
  GoogleAuthProvider,
  confirmPasswordReset,
  createUserWithEmailAndPassword,
  getAuth,
  linkWithPopup,
  onAuthStateChanged,
  reauthenticateWithCredential,
  sendPasswordResetEmail,
  signInWithEmailAndPassword,
  signInWithPopup,
  signOut as firebaseSignout,
  unlink,
  updatePassword,
} from 'firebase/auth';

import { queryClient } from 'api/apiClient';
import useCreateUser from 'api/hooks/user/useCreateUser';
import useShowCurrentUser from 'api/hooks/user/useShowCurrentUser';

initializeApp({
  apiKey: import.meta.env.VITE_FIREBASE_API_KEY,
  authDomain: import.meta.env.VITE_FIREBASE_AUTH_DOMAIN,
});

export enum IdentityProvider {
  Google = 0,
  Facebook = 1,
}

export type SignUpErrorCode = 'emailAlreadyRegistered';

/**
 * 登録フローで発生した、利用者に原因を伝えられるエラー（改修No.56）
 *
 * 従来は原因を問わず同一のエラー文言を表示していたため、実際の原因が
 * 分からなかった。呼び出し側はこの code を見て文言を切り替える。
 */
export class SignUpError extends Error {
  readonly code: SignUpErrorCode;

  constructor(code: SignUpErrorCode) {
    super(code);
    this.name = 'SignUpError';
    this.code = code;
  }
}

type UseAuth = {
  authenticated: boolean;
  email: string;
  name: string;
} & (
  | {
      ready: false;
    }
  | {
      ready: true;
      signUp: (
        email: string,
        password: string,
        request: CreateUserRequest
      ) => Promise<void>;
      signUpWithSocialProvider: (request: CreateUserRequest) => Promise<void>;
      signIn: (email: string, password: string) => Promise<void>;
      signInWithSocialProvider: (
        provider: IdentityProvider
      ) => Promise<boolean>;
      hasPassword: boolean;
      linkedIdentityProviders: IdentityProvider[];
      linkSocialProvider: (provider: IdentityProvider) => Promise<void>;
      unlinkSocialProvider: (provider: IdentityProvider) => Promise<void>;
      changePassword: (
        oldPassword: string,
        newPassword: string
      ) => Promise<void>;
      sendPasswordResetCode: (email: string) => Promise<void>;
      resetPassword: (code: string, password: string) => Promise<void>;
      signOut: () => Promise<void>;
    }
);

const authContext = createContext<UseAuth>({
  authenticated: false,
  email: '',
  name: '',
  ready: false,
});

export const ProvideAuth: React.FC<{ children: React.ReactNode }> = ({
  children,
}) => {
  const auth = useProvideAuth();
  return <authContext.Provider value={auth}>{children}</authContext.Provider>;
};

export const useAuth = () => useContext(authContext);

const useProvideAuth = (): UseAuth => {
  const createUser = useCreateUser();
  const showCurrentUser = useShowCurrentUser({}, { enabled: false }); // update で退会済みかチェックする用
  // ready: 一度でもログインチェックが走ったかどうか
  const [ready, setReady] = useState(false);
  const [authenticated, setAuthenticated] = useState(false);
  const [hasPassword, setHasPassword] = useState(false);
  const [linkedIdentityProviders, setLinkedIdentityProviders] = useState<
    IdentityProvider[]
  >([]);
  const [email, setEmail] = useState('');
  const [name, setName] = useState('');

  const update = async (): Promise<boolean> => {
    try {
      const auth = getAuth();
      await new Promise((resolve, reject) =>
        onAuthStateChanged(auth, resolve, reject)
      );
      queryClient.clear();
      const res = await showCurrentUser.refetch();
      setEmail(
        auth.currentUser?.email ||
          auth.currentUser?.providerData.find((data) => data.email)?.email ||
          ''
      );
      setHasPassword(
        auth.currentUser?.providerData.some(
          (data) => data.providerId === EmailAuthProvider.PROVIDER_ID
        ) ?? false
      );
      setLinkedIdentityProviders(
        auth.currentUser?.providerData
          .map((data) => {
            switch (data.providerId) {
              case GoogleAuthProvider.PROVIDER_ID:
                return IdentityProvider.Google;
              case FacebookAuthProvider.PROVIDER_ID:
                return IdentityProvider.Facebook;
              default:
                return;
            }
          })
          .filter(
            (provider): provider is IdentityProvider => provider !== undefined
          ) ?? []
      );
      setName(auth.currentUser?.displayName || '');
      // 退会済み または SSO での新規登録
      if (res.isError) {
        throw res.error;
      }
      setAuthenticated(true);
      return true;
    } catch {
      setAuthenticated(false);
      return false;
    } finally {
      setReady(true);
    }
  };

  useEffect(() => void update(), []);

  const signUp = async (
    email: string,
    password: string,
    request: CreateUserRequest
  ): Promise<void> => {
    const auth = getAuth();
    // このリクエストで Firebase ユーザーを新規作成したかどうか。
    // 後始末で「消してよいユーザー」を判別するために保持する（改修No.56）
    let isNewFirebaseUser = false;

    try {
      await createUserWithEmailAndPassword(auth, email, password);
      isNewFirebaseUser = true;
    } catch (e) {
      if (
        !(e instanceof FirebaseError && e.code === AuthErrorCodes.EMAIL_EXISTS)
      ) {
        throw e;
      }

      // 同じメールアドレスの Firebase ユーザーが既に存在するケース。
      // 過去に登録が中断して Firebase 側にだけ残ったユーザーは救済したいが、
      // 会員登録済みのユーザーはここでブロックする必要がある。
      try {
        await signInWithEmailAndPassword(auth, email, password);
      } catch {
        // パスワードが一致しない = 登録済みの他人 or パスワード失念。登録は行わない
        throw new SignUpError('emailAlreadyRegistered');
      }

      const currentUser = await showCurrentUser.refetch();
      if (!currentUser.isError) {
        // 会員登録済み＝重複登録。既存ユーザーのアカウントには一切手を触れない
        await firebaseSignout(auth).catch(() => {
          // サインアウト失敗は無視
        });
        setAuthenticated(false);
        setEmail('');
        setName('');
        throw new SignUpError('emailAlreadyRegistered');
      }

      // バックエンドにユーザーが存在しない = Firebase 側にだけ残ったユーザー。
      // 登録を続行して復旧させる（isNewFirebaseUser は false のまま）
    }

    try {
      await createUser.mutateAsync(request);
      await update();
    } catch (e) {
      // バックエンドでのユーザー作成に失敗した場合の後始末。
      // このリクエストで作成した Firebase ユーザーだけを削除する。
      // 既存ユーザーにサインインしている場合に削除するとアカウントを失うため、
      // その場合はサインアウトのみに留める（改修No.56）
      if (isNewFirebaseUser && auth.currentUser) {
        await auth.currentUser.delete().catch(() => {
          // 削除失敗は無視(既に削除されている場合など)
        });
      } else {
        await firebaseSignout(auth).catch(() => {
          // サインアウト失敗は無視
        });
      }
      // ローカルの状態もクリア
      setAuthenticated(false);
      setEmail('');
      setName('');

      // バックエンドが 409（同じメールアドレスの有効な会員が既に居る）を返した場合も
      // 利用者向けの文言に変換する。メール/パスワード登録では、Firebase の設定に関係なく
      // 同じメールの2つ目は EMAIL_EXISTS になるため通常は上の分岐で止まり、ここは
      // 並行登録など分岐をすり抜けた場合の防御（改修No.56）
      if (isAxiosError(e) && e.response?.status === 409) {
        throw new SignUpError('emailAlreadyRegistered');
      }
      throw e;
    }
  };

  const signUpWithSocialProvider = async (
    request: CreateUserRequest
  ): Promise<void> => {
    try {
      await createUser.mutateAsync(request);
      await update();
    } catch (e) {
      // バックエンドでのユーザー作成に失敗した場合、サインアウトして状態をクリア
      const auth = getAuth();
      if (auth.currentUser) {
        await firebaseSignout(auth).catch(() => {
          // サインアウト失敗は無視
        });
        setAuthenticated(false);
        setEmail('');
        setName('');
      }

      // Firebase がプロバイダごとに別アカウントを作る設定（allowDuplicateEmails=true）のとき、
      // Google などで登録しようとした利用者と同じメールのメール/パスワード会員が既に居ると、
      // 重複はバックエンドの 409 で初めて判明する（改修No.56）
      if (isAxiosError(e) && e.response?.status === 409) {
        throw new SignUpError('emailAlreadyRegistered');
      }
      throw e;
    }
  };

  const signIn = async (email: string, password: string): Promise<void> => {
    await signInWithEmailAndPassword(getAuth(), email, password);
    await update();
    const res = await showCurrentUser.refetch();
    // 退会済み
    if (res.isError) {
      throw res.error;
    }
  };

  const identityProviderToAuthProvider = (
    provider: IdentityProvider
  ): AuthProvider => {
    switch (provider) {
      case IdentityProvider.Google: {
        const googleAuthProvider = new GoogleAuthProvider();
        googleAuthProvider.addScope(
          'https://www.googleapis.com/auth/userinfo.email'
        );
        googleAuthProvider.addScope(
          'https://www.googleapis.com/auth/userinfo.profile'
        );
        return googleAuthProvider;
      }
      case IdentityProvider.Facebook: {
        const facebookAuthProvider = new FacebookAuthProvider();
        facebookAuthProvider.addScope('email');
        facebookAuthProvider.addScope('public_profile');
        return facebookAuthProvider;
      }
    }
  };

  const signInWithSocialProvider = async (
    provider: IdentityProvider
  ): Promise<boolean> => {
    await signInWithPopup(getAuth(), identityProviderToAuthProvider(provider));
    return await update();
  };

  const linkSocialProvider = async (
    provider: IdentityProvider
  ): Promise<void> => {
    const auth = getAuth();
    if (!auth.currentUser) {
      throw new Error('No current user');
    }
    await linkWithPopup(
      auth.currentUser,
      identityProviderToAuthProvider(provider)
    );
    await update();
  };

  const unlinkSocialProvider = async (
    provider: IdentityProvider
  ): Promise<void> => {
    const auth = getAuth();
    if (!auth.currentUser) {
      throw new Error('No current user');
    }
    await unlink(
      auth.currentUser,
      identityProviderToAuthProvider(provider).providerId
    );
    await update();
  };

  const changePassword = async (
    oldPassword: string,
    newPassword: string
  ): Promise<void> => {
    const auth = getAuth();
    if (!auth.currentUser) {
      throw new Error('No current user');
    }
    await reauthenticateWithCredential(
      auth.currentUser,
      EmailAuthProvider.credential(email, oldPassword)
    );
    await updatePassword(auth.currentUser, newPassword);
  };

  const sendPasswordResetCode = async (email: string): Promise<void> => {
    await sendPasswordResetEmail(getAuth(), email);
    setEmail(email);
  };

  const resetPassword = async (
    code: string,
    password: string
  ): Promise<void> => {
    await confirmPasswordReset(getAuth(), code, password);
  };

  const signOut = async (): Promise<void> => {
    await firebaseSignout(getAuth());
    queryClient.clear();
    setAuthenticated(false);
    setEmail('');
    setName('');
  };

  return {
    authenticated,
    email,
    name,
    ready,
    signUp,
    signUpWithSocialProvider,
    signInWithSocialProvider,
    signIn,
    hasPassword,
    linkedIdentityProviders,
    linkSocialProvider,
    unlinkSocialProvider,
    changePassword,
    sendPasswordResetCode,
    resetPassword,
    signOut,
  };
};
